import { config } from './config';

// Resolve the embedding page's origin from the referrer, restricted to the
// configured allowlist (config.parentOrigins). This lets the same widget be
// embedded on more than one of our domains (randori-pro.de UND randori.pro)
// while keeping a strict targetOrigin for postMessage — never a blind '*'
// unless nothing is configured.
export function resolveParentOrigin(): string {
  const allow = config.parentOrigins;
  let ref = '';
  try {
    ref = document.referrer ? new URL(document.referrer).origin : '';
  } catch {
    /* ignore malformed referrer */
  }
  // Prefer the real embedding origin when the browser gives it to us: it is by
  // definition the parent, so postMessage is guaranteed to be delivered. An
  // allowlist entry that doesn't exactly match the live host (missing www, wrong
  // hyphen domain, stale config) would otherwise make the browser drop the
  // message silently and the iframe would never resize. If the referrer is one
  // of our known/allowlisted origins we use it; if it's present but unlisted we
  // still use it, since the height + non-PII context we post carry no secret and
  // a mismatched targetOrigin is worse (broken layout) than a permissive one.
  if (ref && allow.includes(ref)) return ref;
  if (ref) return ref;
  // No referrer at all (strict referrer-policy): fall back to the first known
  // origin; '*' only as an absolute last resort so the widget still resizes.
  return allow[0] ?? '*';
}

// Measures the widget height and reports it to the WordPress parent window via
// postMessage, so the embedding snippet can size the <iframe>.
export function startResizeBridge(target: HTMLElement): () => void {
  const targetOrigin = resolveParentOrigin();
  let last = 0;
  const post = () => {
    const height = Math.ceil(target.getBoundingClientRect().height);
    if (height === last) return;
    last = height;
    window.parent.postMessage({ type: 'randori-form-resize', height }, targetOrigin);
  };

  const ro = new ResizeObserver(post);
  ro.observe(target);
  window.addEventListener('load', post);
  // initial nudge once layout settled
  requestAnimationFrame(post);

  return () => {
    ro.disconnect();
    window.removeEventListener('load', post);
  };
}
